import { memo, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Check } from "lucide-react";

import LoaderOverlay from "../../../layout/components/LoaderOverlay";
import { EquipmentListProps } from "../../../../types/baseTypes";
import { RootState } from "../../../../redux/store";
import keyHandoverPickup from "../../../../tenants/kolula-key/assets/img/key_handover_pickup.png";
import keyHandoverPickupAndReturn from "../../../../tenants/kolula-key/assets/img/key_handover_pickup_and_return.png";
import { useKeyHandoverServices } from "../context/KeyHandoverServiceContext";
import { updateBookingQuery } from "../common/url/bookingQuery";
import { getEquipmentName, formatCurrency } from "../../../../util";

/**
 * Card component for service selection, styled like PaymentModelCard
 */
const ServiceCard = memo<{
	item: EquipmentListProps;
	isSelected: boolean;
	onSelect: () => void;
	showIncludedLabel: boolean;
}>(({ item, isSelected, onSelect, showIncludedLabel }) => {
	const { t, i18n } = useTranslation();
	const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);

	const itemImage =
		item.priceCategoryInfo.name === "Key-Pickup-Only"
			? keyHandoverPickup
			: item.priceCategoryInfo.name === "Key-Pickup-And-Return"
			? keyHandoverPickupAndReturn
			: item.featuredImage;

	const title = getEquipmentName(item.name, item.langNames, "title") || item.name;
	const description = getEquipmentName(item.description, item.langNames, "description");

	const lowestPrice = useMemo(() => {
		const prices = item.priceCategoryInfo?.priceScale?.map((ps) => ps.price) ?? [];
		if (prices.length === 0) return null;
		return Math.min(...prices);
	}, [item.priceCategoryInfo?.priceScale]);

	return (
		<button
			onClick={onSelect}
			className={`
				relative w-full p-5 transition-all text-left
				${isSelected ? "bg-primary/5" : "bg-white hover:bg-slate-50"}
			`}
		>
			<div className="flex items-start gap-4">
				<img
					src={itemImage}
					alt={title}
					className="w-10 h-10 flex-shrink-0 rounded-lg object-cover"
				/>

				<div className="flex-1 min-w-0 pr-8">
					<div className="text-base font-semibold text-slate-900">
						{title}
					</div>
					{description && (
						<div className="text-sm text-slate-500 mt-1">
							{description}
						</div>
					)}
					{showIncludedLabel ? (
						<div className="text-sm font-semibold text-primary mt-2">
							{t("book_location.keys.included_in_subscription")}
						</div>
					) : lowestPrice != null && (
						<div className="text-sm font-semibold text-primary mt-2">
							{t("book_location.from")} {formatCurrency(lowestPrice, i18n.language, tenantConfig.currency)} / {t("book_location.day")}
						</div>
					)}
				</div>
			</div>

			{isSelected && (
				<div className="absolute top-1/2 -translate-y-1/2 right-4">
					<div className="bg-primary text-white rounded-full p-1">
						<Check className="h-4 w-4" />
					</div>
				</div>
			)}
		</button>
	);
});

ServiceCard.displayName = "ServiceCard";

function KeyHandoverServiceSelectionStep() {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const {
		services: equipments,
		selected: selectedEquipment,
		isLoading,
		select,
		deselect,
		refetch
	} = useKeyHandoverServices();

	const customerAgreementId = useMemo(() => {
		const params = new URLSearchParams(location.search);
		return params.get("customerAgreementId");
	}, [location.search]);

	const isSubscriptionFlow = !!customerAgreementId;

	const handleCardSelect = useCallback(
		(equipment: EquipmentListProps) => {
			if (selectedEquipment?.id === equipment.id) {
				deselect();
				const newSearch = updateBookingQuery(location.search, { serviceId: undefined });
				navigate(
					{ pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" },
					{ replace: true }
				);
			} else {
				select(equipment);
				const newSearch = updateBookingQuery(location.search, { serviceId: equipment.id });
				navigate(
					{ pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" },
					{ replace: true }
				);
			}
		},
		[select, deselect, selectedEquipment, location.search, location.pathname, navigate]
	);

	// Refetch equipments when customerAgreementId changes
	useEffect(() => {
		if (customerAgreementId) {
			const timeout = setTimeout(() => {
				if (typeof window !== "undefined" && window.requestIdleCallback) {
					window.requestIdleCallback(() => {
						refetch();
					});
				} else {
					refetch();
				}
			}, 100);
			return () => clearTimeout(timeout);
		}
	}, [customerAgreementId, refetch]);

	// Show loading state when equipments haven't loaded yet
	if (isLoading && !equipments) {
		return (
			<div
				className="flex flex-col relative isolate items-center justify-center py-12"
				role="region"
				aria-label={t("book_location.keys.service", { defaultValue: "Service auswählen" })}
				aria-busy="true"
			>
				<LoaderOverlay open={true} />
			</div>
		);
	}

	return (
		<div
			className="flex flex-col gap-4 relative"
			role="radiogroup"
			aria-label={t("book_location.keys.service", { defaultValue: "Service auswählen" })}
			aria-busy={isLoading}
		>
			{equipments?.map((item: EquipmentListProps) => {
				if (!item.priceCategoryInfo) return null;
				const isSelected = selectedEquipment?.id === item.id;

				return (
					<div
						key={item.id}
						className={`
							rounded-xl border transition-all overflow-hidden
							${isSelected
								? "border-primary ring-1 ring-primary/20 shadow-md"
								: "border-slate-200"
							}
						`}
					>
						<ServiceCard
							item={item}
							isSelected={isSelected}
							onSelect={() => handleCardSelect(item)}
							showIncludedLabel={isSubscriptionFlow}
						/>
					</div>
				);
			})}
			{/* The overlay is its own live region; it renders into <body>. */}
			<LoaderOverlay open={isLoading} />
		</div>
	);
}

export default memo(KeyHandoverServiceSelectionStep);
